import * as styles from "../styles";
import { ConfirmDialog } from "./Dialogs";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React, { useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const isValidName = (name) => !!name.trim();
const NameDialog = React.memo(
  ({
    open,
    onClose,
    onSave,
    onEdit,
    reportName,
    onReportNameChange,
    reportDescription,
    onReportDescriptionChange,
    originalReportName = "",
    originalReportDescription = "",
    error,
    onErrorClear,
    isSaving,
    title = "Save Report As",
    saveButtonLabel = "Save",
    savingButtonLabel = "Saving...",
    onDelete,
    isDeleting = false,
    isReadOnly = false,
    allUsers = false,
    readOnly = false,
    canWriteMetadata = true,
    onShareSettingsChange,
    isSavingShare = false,
    shareError = "",
    onShareErrorClear,
    isSaveDisabled = false,
  }) => {
    const translateLabels = staticDataHooks.useTranslator("uiLabels");
    const showDelete = onDelete && title === "Edit Report";
    const hasMetadataChanges =
      reportName.trim() !== originalReportName.trim() ||
      reportDescription.trim() !== originalReportDescription.trim();
    const [localAllUsers, setLocalAllUsers] = useState(allUsers);
    const [localReadOnly, setLocalReadOnly] = useState(readOnly);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [controlAnchor, setControlAnchor] = useState(null);

    React.useEffect(() => {
      setLocalAllUsers(allUsers);
      setLocalReadOnly(readOnly);
      setControlAnchor(null);
      setDeleteConfirmOpen(false);
    }, [allUsers, readOnly, open]);
    const clearShareError = () => {
      if (shareError && onShareErrorClear) onShareErrorClear();
    };
    const handleAllUsersChange = (event) => {
      clearShareError();
      setLocalAllUsers(event.target.checked);
    };
    const handleReadOnlyChange = (event) => {
      clearShareError();
      setLocalReadOnly(event.target.checked);
    };
    const handleApplyShareSettings = async () => {
      if (!onShareSettingsChange) return;
      const saved = await onShareSettingsChange({
        allUsers: localAllUsers,
        readOnly: localReadOnly,
      });
      // A failed save leaves the report as it was, so the boxes must say so.
      if (saved === false) {
        setLocalAllUsers(allUsers);
        setLocalReadOnly(readOnly);
      }
    };
    const hasShareChanges =
      localAllUsers !== allUsers || localReadOnly !== readOnly;

    const isActionRunning = isSaving || isDeleting || isSavingShare;
    const handleDeleteClick = () => {
      setDeleteConfirmOpen(true);
    };
    const handleClose = () => {
      if (isActionRunning) {
        return;
      }

      onClose();
    };
    const handleDeleteConfirm = () => {
      setDeleteConfirmOpen(false);
      if (onDelete) {
        onDelete();
      }
    };
    const handleDeleteCancel = () => {
      setDeleteConfirmOpen(false);
    };
    const deleteLabel = translateLabels(labels.NAME_DIALOG_DELETE, {
      default: "Delete report",
    });
    return (
      <React.Fragment>
        <ConfirmDialog
          open={deleteConfirmOpen}
          title={deleteLabel}
          message={translateLabels(labels.NAME_DIALOG_DELETE_CONFIRM_MESSAGE, {
            default: `Are you sure you want to delete "${reportName}"?`,
          })}
          confirmLabel={deleteLabel}
          confirmColor={"error"}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
        <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
          <DialogTitle>
            {title}
            <IconButton
              aria-label={translateLabels(labels.NAME_DIALOG_CLOSE, {
                default: "close",
              })}
              onClick={handleClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
              size={"small"}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={styles.dialogContent}>
              <TextField
                autoFocus
                label={translateLabels(labels.NAME_DIALOG_REPORT_NAME, {
                  default: "Report Name",
                })}
                fullWidth
                variant={"outlined"}
                value={reportName}
                onChange={(e) => {
                  onReportNameChange(e.target.value);
                  if (error) onErrorClear();
                }}
                error={!!error}
                helperText={error}
                required
                disabled={isActionRunning}
              />
              <TextField
                label={translateLabels(labels.NAME_DIALOG_DESCRIPTION, {
                  default: "Description",
                })}
                fullWidth
                variant={"outlined"}
                multiline
                rows={3}
                value={reportDescription}
                onChange={(e) => onReportDescriptionChange(e.target.value)}
                placeholder={translateLabels(
                  labels.NAME_DIALOG_DESCRIPTION_PLACEHOLDER,
                  { default: "Optional description for this report" }
                )}
                disabled={isActionRunning}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={styles.dialogActions}>
            <Box>
              {showDelete && (
                <React.Fragment>
                  <Button
                    onClick={(e) => {
                      clearShareError();
                      setControlAnchor(e.currentTarget);
                    }}
                    isDisabled={isActionRunning}
                    startIcon={<SettingsIcon />}
                  >
                    {translateLabels(labels.NAME_DIALOG_SHARING_AND_DELETE, {
                      default: "Sharing and delete",
                    })}
                  </Button>
                  <Popover
                    open={Boolean(controlAnchor)}
                    anchorEl={controlAnchor}
                    onClose={() => setControlAnchor(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                    slotProps={{
                      paper: { sx: { minWidth: 220, maxWidth: 360 } },
                    }}
                  >
                    <Stack sx={{ p: 2 }} spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localReadOnly}
                            onChange={handleReadOnlyChange}
                            disabled={isActionRunning || !canWriteMetadata}
                            size={"small"}
                          />
                        }
                        label={
                          <Typography variant={"body2"}>
                            {"Read Only"}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localAllUsers}
                            onChange={handleAllUsersChange}
                            disabled={isActionRunning || !canWriteMetadata}
                            size={"small"}
                          />
                        }
                        label={
                          <Typography variant={"body2"}>
                            {"All Users"}
                          </Typography>
                        }
                      />
                      {shareError && (
                        <Alert
                          severity={"error"}
                          data-testid={"share-settings-error"}
                        >
                          {translateLabels(labels.NAME_DIALOG_SHARE_FAILED, {
                            default: "Couldn't save the sharing settings.",
                          })}{" "}
                          {shareError}
                        </Alert>
                      )}
                      <Button
                        size={"small"}
                        onClick={handleApplyShareSettings}
                        isDisabled={
                          !hasShareChanges ||
                          isActionRunning ||
                          !canWriteMetadata
                        }
                        isLoading={isSavingShare}
                        startIcon={<SaveIcon />}
                      >
                        {"Save"}
                      </Button>
                      <Divider />
                      <Button
                        onClick={handleDeleteClick}
                        color={"error"}
                        size={"small"}
                        isDisabled={
                          isActionRunning || isReadOnly || localReadOnly
                        }
                        isLoading={isDeleting}
                        startIcon={<DeleteIcon />}
                      >
                        {deleteLabel}
                      </Button>
                    </Stack>
                  </Popover>
                </React.Fragment>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              {onEdit && (
                <Button
                  onClick={onEdit}
                  color={"primary"}
                  sx={{ width: 100 }}
                  isDisabled={isActionRunning}
                  startIcon={<EditIcon />}
                >
                  {translateLabels(labels.NAME_DIALOG_EDIT, {
                    default: "Edit",
                  })}
                </Button>
              )}
              <Button
                onClick={onSave}
                color={"primary"}
                sx={{ width: 100 }}
                isLoading={isSaving}
                isDisabled={
                  isActionRunning ||
                  isReadOnly ||
                  !isValidName(reportName) ||
                  (onEdit && !hasMetadataChanges) ||
                  isSaveDisabled
                }
              >
                {isSaving ? savingButtonLabel : saveButtonLabel}
              </Button>
              <Button
                onClick={handleClose}
                color={"secondary"}
                sx={{ width: 100 }}
                isDisabled={isActionRunning}
              >
                {translateLabels(labels.NAME_DIALOG_CANCEL, {
                  default: "Cancel",
                })}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
);
NameDialog.displayName = "NameDialog";
export default NameDialog;
